import React, { useState, useEffect } from 'react';
import { API_URL } from "../config";
import { useParams, useNavigate, Link } from 'react-router-dom';
// Payment terms come from ONE shared list (see the header of that file for why).
import { paymentTermsOptions } from '../utils/paymentTerms';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaBuilding, FaUserTie, FaEnvelope, FaPhone, 
  FaIdCard, FaTruck, FaTrailer, FaFileAlt, FaShieldAlt, 
  FaUniversity, FaUsers, FaClock, FaSpinner, FaCheckCircle,
  FaTimes, FaEye, FaDownload, FaExternalLinkAlt, FaEdit,
  FaTrash, FaStar, FaStarHalfAlt, FaRegStar, FaPlus,
  FaFilePdf, FaFileImage, FaImage, FaMapMarkerAlt,
  FaWhatsapp, FaGlobe, FaBriefcase, FaCalendarAlt, FaBox,
  FaWeight, FaExclamationTriangle, FaUpload, FaSave
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import EditInsuranceModal from '../components/EditInsuranceModal';
import UploadDocumentModal from '../components/UploadDocumentModal';
import ConfirmDialog from '../components/ConfirmDialog';
import CompanyUsersCard from '../components/CompanyUsersCard';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

// Shared fetch helper: prefixes the API URL + attaches the auth token.
// Fixes "html instead of json" errors from bare relative fetches.
const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
};

// ============================================
// Editable row - MUST stay at MODULE scope.
// It used to be declared INSIDE the component, so React saw a brand-new component type
// on every keystroke and re-mounted the whole subtree - which is why the input lost
// focus after the first letter (type one letter, click, type one letter...).
// Hoisting it here keeps the component identity stable and the focus intact.
// ============================================
const Editable = ({ k, label, type = 'text', options, editData, setEditData, isEditing, subbie }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm text-gray-500">{label}</span>
    {isEditing ? (
      type === 'select' ? (
        <select
          value={editData[k] || ''}
          onChange={(e) => setEditData({ ...editData, [k]: e.target.value })}
          className="text-sm border border-gray-300 rounded-lg px-2 py-1"
        >
          {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          type={type}
          value={editData[k] || ''}
          onChange={(e) => setEditData({ ...editData, [k]: e.target.value })}
          className="w-1/2 text-sm border border-gray-300 rounded-lg px-2 py-1 text-right"
        />
      )
    ) : (
      <span className="text-sm font-medium text-navy">{subbie[k] || 'N/A'}</span>
    )}
  </div>
);

function SubcontractorDetailPage({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subbie, setSubbie] = useState(null);
  const [user, setUser] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  const [loads, setLoads] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [insuranceType, setInsuranceType] = useState('git');
  const [insuranceData, setInsuranceData] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
const { isLoaded } = useGoogleMaps();

  // ✅ Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Payment terms are defined ONCE in utils/paymentTerms.js — do not re-type a list here.
  // The options for the dropdown are built from the SHARED list plus whatever value this
  // subbie already has, so an older/other value stays selectable and is never silently
  // replaced by the first option when the form is saved.
  const termsOptions = paymentTermsOptions(subbie?.payment_terms);

  const documentTypes = [
    { key: 'cipc_document', display_name: 'CIPC Registration Certificate', required: true },
    { key: 'cipc_annual_return', display_name: 'CIPC Annual Return', required: true },
    { key: 'shareholder_register', display_name: 'Shareholder Register', required: true },
    { key: 'git_insurance', display_name: 'GIT Insurance Certificate', required: true },
    { key: 'public_liability', display_name: 'Public Liability Insurance', required: true },
    { key: 'operator_card', display_name: 'Operator Card', required: true },
    { key: 'bank_confirmation', display_name: 'Bank Confirmation Letter', required: true },
    { key: 'bank_statement', display_name: 'Bank Statement', required: true },
    { key: 'tax_clearance', display_name: 'Tax Clearance Certificate', required: true },
    { key: 'bbbee_certificate', display_name: 'B-BBEE Certificate', required: false },
    { key: 'id_document', display_name: 'ID Document', required: true },
    { key: 'liveness_photo', display_name: 'Liveness Photo', required: true }
  ];

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchSubbieDetail();
  }, [id]);

  const fetchSubbieDetail = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/subcontractors/${id}`);
      const data = response;
      if (data.success) {
        setSubbie(data.subcontractor);
      } else {
        toast.error('Failed to load subcontractor details');
        navigate('/subcontractors');
        return;
      }

      const loadsRes = await apiFetch(`/loads?subcontractor_id=${id}`);
      const loadsData = loadsRes;
      if (loadsData.success) {
        setLoads(loadsData.loads || []);
      }

      const trucksRes = await apiFetch(`/trucks?subcontractor_id=${id}`);
      const trucksData = trucksRes;
      if (trucksData.success) {
        setTrucks(trucksData.trucks || []);
      }

      const trailersRes = await apiFetch(`/trailers?subcontractor_id=${id}`);
      const trailersData = trailersRes;
      if (trailersData.success) {
        setTrailers(trailersData.trailers || []);
      }

      const driversRes = await apiFetch(`/drivers?subcontractor_id=${id}`);
      const driversData = driversRes;
      if (driversData.success) {
        setDrivers(driversData.drivers || []);
      }

    } catch (err) {
      console.error('Error fetching subbie:', err);
      toast.error('Failed to load details');
      navigate('/subcontractors');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInsurance = (type) => {
    const dataMap = {
      'git': {
        provider: subbie.git_insurance_provider || '',
        policy_number: subbie.git_policy_number || '',
        expiry: subbie.git_insurance_expiry || '',
        cover_amount: subbie.git_cover_amount || ''
      },
      'pl': {
        provider: subbie.public_liability_provider || '',
        policy_number: subbie.public_liability_policy_number || '',
        expiry: subbie.public_liability_expiry || '',
        cover_amount: subbie.public_liability_cover_amount || ''
      },
      'tax': {
        provider: '',
        policy_number: '',
        expiry: subbie.tax_clearance_expiry || '',
        cover_amount: ''
      }
    };
    setInsuranceType(type);
    setInsuranceData(dataMap[type]);
    setShowInsuranceModal(true);
  };

  const handleSaveInsurance = async (data) => {
    const endpoints = {
      'git': `/subcontractors/${id}/insurance/git`,
      'pl': `/subcontractors/${id}/insurance/pl`,
      'tax': `/subcontractors/${id}/insurance/tax`
    };

    const payload = {
      ...data,
      expiry: data.expiry
    };

    const response = await apiFetch(endpoints[insuranceType], {
      method: 'PUT',
      body: JSON.stringify(payload)
    });

    const result = response;
    if (result.success) {
      toast.success('✅ Insurance details updated!');
      fetchSubbieDetail();
    } else {
      throw new Error(result.error || 'Failed to update insurance');
    }
  };

  const handleUploadInsurance = async (type, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('certificate', file);
      formData.append('type', type);

      const response = await apiFetch(`/subcontractors/${id}/insurance/upload`, {
        method: 'POST',
        body: formData
      });

      const data = response;
      if (data.success) {
        toast.success('✅ Insurance certificate uploaded successfully!');
        fetchSubbieDetail();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Failed to upload certificate');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleUploadDocument = async (docType, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('type', docType);

      const response = await apiFetch(`/subcontractors/${id}/documents`, {
        method: 'POST',
        body: formData
      });

      const data = response;
      if (data.success) {
        toast.success('✅ Document uploaded successfully!');
        fetchSubbieDetail();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Failed to upload document');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const totalTonnage = loads.reduce((sum, l) => sum + (l.weight_mt || 0), 0);
  const totalLoads = loads.length;
  const activeTrucks = trucks.filter(t => 
    t.current_status === 'available' || t.current_status === 'en_route'
  ).length;

  const checkExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'EXPIRED', color: 'text-red-600 bg-red-50', icon: <FaExclamationTriangle className="text-red-500" />, days: daysUntilExpiry };
    } else if (daysUntilExpiry < 30) {
      return { status: 'expiring', label: `${daysUntilExpiry} days remaining`, color: 'text-yellow-600 bg-yellow-50', icon: <FaClock className="text-yellow-500" />, days: daysUntilExpiry };
    } else {
      return { status: 'valid', label: 'Valid', color: 'text-green-600 bg-green-50', icon: <FaCheckCircle className="text-green-500" />, days: daysUntilExpiry };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'approved': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600',
      'suspended': 'bg-gray-100 text-gray-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending Approval',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'suspended': 'Suspended'
    };
    return labels[status] || status;
  };

  const getDocumentUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return window.getServerUrl(url);
  };

  const viewDocument = (docUrl, docName) => {
    if (!docUrl) {
      toast.warning('No document uploaded');
      return;
    }
    const fullUrl = getDocumentUrl(docUrl);
    if (fullUrl) {
      window.open(fullUrl, '_blank');
    } else {
      toast.error('Could not open document');
    }
  };

  const downloadDocument = (url, filename) => {
    if (!url) {
      toast.warning('No document uploaded');
      return;
    }
    const link = document.createElement('a');
    link.href = getDocumentUrl(url);
    link.download = filename || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getDocumentIcon = (url) => {
    if (!url) return <FaFileAlt className="text-gray-400" />;
    if (url.toLowerCase().includes('.pdf')) return <FaFilePdf className="text-red-500" />;
    if (url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg') || url.toLowerCase().includes('.png')) {
      return <FaFileImage className="text-blue-500" />;
    }
    return <FaFileAlt className="text-gray-400" />;
  };

  const getDocumentLabel = (url) => {
    if (!url) return 'No document';
    if (url.toLowerCase().includes('.pdf')) return 'PDF';
    if (url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg')) return 'JPEG';
    if (url.toLowerCase().includes('.png')) return 'PNG';
    return 'Document';
  };

  const getDocuments = () => {
    if (!subbie) return [];
    const docMap = {
      'cipc_document_url': { label: 'CIPC Registration', key: 'cipc_document' },
      'cipc_annual_return_url': { label: 'CIPC Annual Return', key: 'cipc_annual_return' },
      'shareholder_register_url': { label: 'Shareholder Register', key: 'shareholder_register' },
      'git_insurance_url': { label: 'GIT Insurance', key: 'git_insurance' },
      'public_liability_url': { label: 'Public Liability', key: 'public_liability' },
      'operator_card_url': { label: 'Operator Card', key: 'operator_card' },
      'bank_confirmation_url': { label: 'Bank Confirmation', key: 'bank_confirmation' },
      'bank_statement_url': { label: 'Bank Statement', key: 'bank_statement' },
      'tax_clearance_certificate_url': { label: 'Tax Clearance', key: 'tax_clearance' },
      'bbbee_certificate_url': { label: 'B-BBEE Certificate', key: 'bbbee_certificate' },
      'id_document_url': { label: 'ID Document', key: 'id_document' },
      'liveness_photo_url': { label: 'Liveness Photo', key: 'liveness_photo' }
    };

    const docs = [];
    for (const [key, info] of Object.entries(docMap)) {
      if (subbie[key]) {
        docs.push({
          label: info.label,
          url: subbie[key],
          key: info.key
        });
      }
    }
    return docs;
  };

  const getExpiringItems = () => {
    if (!subbie) return [];
    const items = [];
    
    const gitExpiry = checkExpiry(subbie.git_insurance_expiry);
    if (gitExpiry && gitExpiry.status !== 'valid') {
      items.push({ type: 'GIT Insurance', expiry: subbie.git_insurance_expiry, status: gitExpiry });
    }
    
    const plExpiry = checkExpiry(subbie.public_liability_expiry);
    if (plExpiry && plExpiry.status !== 'valid') {
      items.push({ type: 'Public Liability', expiry: subbie.public_liability_expiry, status: plExpiry });
    }
    
    const taxExpiry = checkExpiry(subbie.tax_clearance_expiry);
    if (taxExpiry && taxExpiry.status !== 'valid') {
      items.push({ type: 'Tax Clearance', expiry: subbie.tax_clearance_expiry, status: taxExpiry });
    }
    
    return items;
  };

  const handleEdit = () => {
    if (isEditing) {
      setIsEditing(false);
      return;
    }
    if (!subbie) return;
    setEditData({
      company_name: subbie.company_name || '',
      trading_name: subbie.trading_name || '',
      registration_number: subbie.registration_number || '',
      vat_number: subbie.vat_number || '',
      physical_address: subbie.physical_address || '',
      contact_person: subbie.contact_person || '',
      contact_phone: subbie.contact_phone || '',
      contact_email: subbie.contact_email || '',
      company_phone: subbie.company_phone || '',
      company_website: subbie.company_website || '',
      // Never fabricate a term: if the subbie has none recorded, leave it empty so the
      // field reads "Not set" instead of claiming 30 Days nobody agreed to.
      payment_terms: subbie.payment_terms || '',
      credit_limit: subbie.credit_limit || 0,
      operating_areas: subbie.operating_areas || '',
      bank_name: subbie.bank_name || '',
      bank_branch: subbie.bank_branch || '',
      bank_account_number: subbie.bank_account_number || '',
      bank_account_type: subbie.bank_account_type || '',
      bank_branch_code: subbie.bank_branch_code || ''
    });
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        company_name: editData.company_name,
        trading_name: editData.trading_name || null,
        registration_number: editData.registration_number || null,
        vat_number: editData.vat_number || null,
        physical_address: editData.physical_address || null,
        contact_person: editData.contact_person,
        contact_phone: editData.contact_phone,
        contact_email: editData.contact_email,
        company_phone: editData.company_phone || null,
        company_website: editData.company_website || null,
        payment_terms: editData.payment_terms || null,
        credit_limit: parseFloat(editData.credit_limit) || 0,
        operating_areas: editData.operating_areas || null,
        bank_name: editData.bank_name || null,
        bank_branch: editData.bank_branch || null,
        bank_account_number: editData.bank_account_number || null,
        bank_account_type: editData.bank_account_type || null,
        bank_branch_code: editData.bank_branch_code || null
      };
      const data = await apiFetch(`/subcontractors/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      if (data.success) {
        toast.success('✅ Subcontractor updated successfully');
        setIsEditing(false);
        fetchSubbieDetail();
      } else {
        toast.error(data.error || 'Failed to update subcontractor');
      }
    } catch (err) {
      console.error('Error updating subbie:', err);
      toast.error('Failed to update subcontractor');
    } finally {
      setSavingEdit(false);
    }
  };

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleDelete = () => setConfirmDeleteOpen(true);

  const performDelete = async () => {
    try {
      const response = await apiFetch(`/subcontractors/${id}`, {
        method: 'DELETE'
      });
      const data = response;
      if (data.success) {
        toast.success('Subcontractor deleted successfully');
        navigate('/subcontractors');
      } else {
        toast.error(data.error || 'Failed to delete subcontractor');
      }
    } catch (err) {
      console.error('Error deleting:', err);
      toast.error('Failed to delete subcontractor');
    }
    setConfirmDeleteOpen(false);
  };

  if (loading) {
    return (
      <AdminLayout activeTab="subcontractors" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading subcontractor details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!subbie) {
    return (
      <AdminLayout activeTab="subcontractors" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Subcontractor Not Found</h2>
          <p className="text-gray-500">The subcontractor you're looking for doesn't exist.</p>
          <Link to="/subcontractors" className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            Back to Subcontractors
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const documents = getDocuments();
  const expiringItems = getExpiringItems();
  const gitExpiry = checkExpiry(subbie.git_insurance_expiry);
  const plExpiry = checkExpiry(subbie.public_liability_expiry);
  const taxExpiry = checkExpiry(subbie.tax_clearance_expiry);


  // Editable now lives at MODULE scope (top of this file) so typing cannot re-mount the
  // input and steal the focus after every keystroke.

  return (
    <AdminLayout activeTab="subcontractors" onLogout={onLogout} user={user}>
      {/* Header with Back Button */}
      <div className="flex items-center gap-4 mb-4">
        <Link to="/subcontractors" className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: '#0A1929' }}>
          <FaArrowLeft size={20} />
        </Link>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>{subbie.company_name}</h1>
            <span className={getStatusBadge(subbie.verification_status)}>
              {getStatusLabel(subbie.verification_status)}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            Reg: {subbie.registration_number || 'N/A'}
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          {isEditing && (
            <button
              onClick={handleSaveEdit}
              disabled={savingEdit}
              className="px-4 py-2 bg-green text-white rounded-lg text-sm hover:bg-green transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FaSave size={14} /> {savingEdit ? 'Saving...' : 'Save Changes'}
            </button>
          )}
          <button
            onClick={handleEdit}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:border-green hover:text-green transition-colors flex items-center gap-2"
          >
            <FaEdit size={14} /> {isEditing ? 'Cancel' : 'Edit'}
          </button>
        </div>
      </div>

      {expiringItems.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <h3 className="font-semibold text-red-700 flex items-center gap-2 mb-2">
            <FaExclamationTriangle /> Items Requiring Attention
          </h3>
          <div className="space-y-1">
            {expiringItems.map((item, index) => (
              <p key={index} className="text-sm text-red-600">
                ⚠️ {item.type} {item.status.status === 'expired' ? 'has EXPIRED' : `expires in ${item.status.days} days`} ({formatDate(item.expiry)})
              </p>
            ))}
          </div>
        </div>
      )}

      {/* KPI METRICS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
              <FaBox size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Loads</p>
              <p className="text-xl font-bold text-navy">{totalLoads}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
              <FaWeight size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Tonnage</p>
              <p className="text-xl font-bold text-navy">{totalTonnage.toFixed(0)} MT</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600">
              <FaTruck size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Tractors</p>
              <p className="text-xl font-bold text-navy">{trucks.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
              <FaCheckCircle size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Active Trucks</p>
              <p className="text-xl font-bold text-navy">{activeTrucks}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'profile' ? 'border-b-2 border-green-500 text-green-600' : 'text-gray-500 hover:text-navy'
          }`}
        >
          <FaBuilding className="inline mr-2" /> Profile
        </button>
        <button
          onClick={() => setActiveTab('fleet')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'fleet' ? 'border-b-2 border-green-500 text-green-600' : 'text-gray-500 hover:text-navy'
          }`}
        >
          <FaTruck className="inline mr-2" /> Fleet
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'documents' ? 'border-b-2 border-green-500 text-green-600' : 'text-gray-500 hover:text-navy'
          }`}
        >
          <FaFileAlt className="inline mr-2" /> Documents ({documents.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
                <FaBuilding className="inline mr-2" style={{ color: '#00A86B' }} />
                Company Information
              </h2>
              <div className="space-y-3">
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="company_name" label="Company Name" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="trading_name" label="Trading Name" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="registration_number" label="Registration Number" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="vat_number" label="VAT Number" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="years_in_business" label="Years in Business" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="payment_terms" label="Payment Terms" type="select" options={termsOptions} />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="physical_address" label="Address" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="operating_areas" label="Operating Areas" />
              </div>
            </div>

            <CompanyUsersCard
              companyType="subcontractor"
              companyId={id}
              companyName={subbie?.company_name}
              className="rounded-xl border p-6"
            />

            <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
                <FaUserTie className="inline mr-2" style={{ color: '#00A86B' }} />
                Contact Information
              </h2>
              <div className="space-y-3">
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="contact_person" label="Contact Person" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="position" label="Position" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="contact_phone" label="Phone" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="contact_email" label="Email" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="company_phone" label="Company Phone" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="company_website" label="Website" />
              </div>
              {/* LOCATION & MAP */}
              <div className="bg-white rounded-xl border p-6 lg:col-span-2" style={{ borderColor: '#E8ECEF' }}>
                <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
                  <FaMapMarkerAlt className="inline mr-2" style={{ color: '#00A86B' }} />
                  Location & Map
                </h2>

                {(() => {
                  const main = (subbie.company_lat && subbie.company_lng) ? {
                    lat: Number(subbie.company_lat),
                    lng: Number(subbie.company_lng),
                    label: subbie.company_name || 'Main',
                    address: subbie.company_address || ''
                  } : null;
                  const extras = (Array.isArray(subbie.locations) ? subbie.locations : [])
                    .filter(l => l && l.lat && l.lng)
                    .map(l => ({ lat: Number(l.lat), lng: Number(l.lng), label: l.label || 'Extra', address: l.address || '' }));
                  const markers = [main, ...extras].filter(Boolean);
                  const center = markers[0] || { lat: -26.2041, lng: 28.0473 };

                  return (
                    <div>
                      {isLoaded && markers.length > 0 ? (
                        <div className="rounded-lg overflow-hidden border" style={{ borderColor: '#E8ECEF', height: '280px' }}>
                          <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={center}
                            zoom={main ? 14 : 6}
                            options={{ mapTypeControl: false, streetViewControl: false }}
                          >
                            {markers.map((m, i) => (
                              <Marker key={i} position={{ lat: m.lat, lng: m.lng }} title={m.address || m.label} />
                            ))}
                          </GoogleMap>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No location set yet. The subbie can set their address with the map picker on their profile.</p>
                      )}

                      <div className="mt-4 space-y-2">
                        {main && (
                          <div className="flex items-start gap-2 text-sm">
                            <FaMapMarkerAlt className="mt-0.5 text-green-600 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-navy">{subbie.company_name}</p>
                              <p className="text-gray-600">{main.address || 'No address text'}</p>
                            </div>
                          </div>
                        )}
                        {extras.map((l, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            <FaMapMarkerAlt className="mt-0.5 text-blue-600 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-navy">{l.label}</p>
                              <p className="text-gray-600">{l.address}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

            </div>

            <div className="bg-white rounded-xl border p-6 lg:col-span-2" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
                <FaShieldAlt className="inline mr-2" style={{ color: '#00A86B' }} />
                Insurance Details
              </h2>
              
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-medium text-navy flex items-center gap-2">
                    🚛 GIT Insurance (Goods in Transit)
                    {gitExpiry && (
                      <span className={`text-xs px-2 py-1 rounded-full ${gitExpiry.color}`}>
                        {gitExpiry.icon} {gitExpiry.label}
                      </span>
                    )}
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditInsurance('git')}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#2563EB',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1D4ED8'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                    >
                      <FaEdit size={10} /> Edit
                    </button>
                    <button
                      onClick={() => {
                        setInsuranceType('git');
                        setShowUploadModal(true);
                      }}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#00A86B',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                    >
                      <FaUpload size={10} /> Upload
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Provider:</span> <span className="font-medium">{subbie.git_insurance_provider || 'N/A'}</span></div>
                  <div><span className="text-gray-500">Policy #:</span> <span className="font-medium">{subbie.git_policy_number || 'N/A'}</span></div>
                  <div><span className="text-gray-500">Cover Amount:</span> <span className="font-medium">{subbie.git_cover_amount ? `R${subbie.git_cover_amount.toLocaleString()}` : 'N/A'}</span></div>
                  <div><span className="text-gray-500">Expiry:</span> <span className="font-medium">{formatDate(subbie.git_insurance_expiry)}</span></div>
                </div>
                {subbie.git_insurance_url && (
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => viewDocument(subbie.git_insurance_url, 'GIT Insurance')}
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <FaEye size={10} /> View Certificate
                    </button>
                    <button
                      onClick={() => downloadDocument(subbie.git_insurance_url, 'git_insurance.pdf')}
                      className="text-xs text-green-600 hover:underline flex items-center gap-1"
                    >
                      <FaDownload size={10} /> Download
                    </button>
                  </div>
                )}
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-medium text-navy flex items-center gap-2">
                    🏢 Public Liability Insurance
                    {plExpiry && (
                      <span className={`text-xs px-2 py-1 rounded-full ${plExpiry.color}`}>
                        {plExpiry.icon} {plExpiry.label}
                      </span>
                    )}
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditInsurance('pl')}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#2563EB',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1D4ED8'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                    >
                      <FaEdit size={10} /> Edit
                    </button>
                    <button
                      onClick={() => {
                        setInsuranceType('pl');
                        setShowUploadModal(true);
                      }}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#00A86B',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                    >
                      <FaUpload size={10} /> Upload
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Provider:</span> <span className="font-medium">{subbie.public_liability_provider || 'N/A'}</span></div>
                  <div><span className="text-gray-500">Policy #:</span> <span className="font-medium">{subbie.public_liability_policy_number || 'N/A'}</span></div>
                  <div><span className="text-gray-500">Cover Amount:</span> <span className="font-medium">{subbie.public_liability_cover_amount ? `R${subbie.public_liability_cover_amount.toLocaleString()}` : 'N/A'}</span></div>
                  <div><span className="text-gray-500">Expiry:</span> <span className="font-medium">{formatDate(subbie.public_liability_expiry)}</span></div>
                </div>
                {subbie.public_liability_url && (
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => viewDocument(subbie.public_liability_url, 'Public Liability')}
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <FaEye size={10} /> View Certificate
                    </button>
                    <button
                      onClick={() => downloadDocument(subbie.public_liability_url, 'public_liability.pdf')}
                      className="text-xs text-green-600 hover:underline flex items-center gap-1"
                    >
                      <FaDownload size={10} /> Download
                    </button>
                  </div>
                )}
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-medium text-navy flex items-center gap-2">
                    📄 Tax Clearance Certificate
                    {taxExpiry && (
                      <span className={`text-xs px-2 py-1 rounded-full ${taxExpiry.color}`}>
                        {taxExpiry.icon} {taxExpiry.label}
                      </span>
                    )}
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditInsurance('tax')}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#2563EB',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1D4ED8'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                    >
                      <FaEdit size={10} /> Edit
                    </button>
                    <button
                      onClick={() => {
                        setInsuranceType('tax');
                        setShowUploadModal(true);
                      }}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#00A86B',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                    >
                      <FaUpload size={10} /> Upload
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Expiry:</span> <span className="font-medium">{formatDate(subbie.tax_clearance_expiry)}</span></div>
                </div>
                {subbie.tax_clearance_certificate_url && (
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => viewDocument(subbie.tax_clearance_certificate_url, 'Tax Clearance')}
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <FaEye size={10} /> View Certificate
                    </button>
                    <button
                      onClick={() => downloadDocument(subbie.tax_clearance_certificate_url, 'tax_clearance.pdf')}
                      className="text-xs text-green-600 hover:underline flex items-center gap-1"
                    >
                      <FaDownload size={10} /> Download
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border p-6 lg:col-span-2" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
                <FaUniversity className="inline mr-2" style={{ color: '#00A86B' }} />
                Banking Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="bank_name" label="Bank Name" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="bank_branch" label="Branch" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="bank_account_number" label="Account Number" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="bank_account_type" label="Account Type" />
                <Editable editData={editData} setEditData={setEditData} isEditing={isEditing} subbie={subbie} k="bank_branch_code" label="Branch Code" />
              </div>
              {!subbie.bank_name && !subbie.bank_account_number && (
                <p className="text-sm text-gray-400 text-center mt-4">No banking details provided</p>
              )}
            </div>

            {(subbie.reference_1_name || subbie.reference_2_name) && (
              <div className="bg-white rounded-xl border p-6 lg:col-span-2" style={{ borderColor: '#E8ECEF' }}>
                <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
                  <FaUsers className="inline mr-2" style={{ color: '#00A86B' }} />
                  References
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {subbie.reference_1_name && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium text-navy mb-2">Reference 1</h3>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-500">Name:</span> {subbie.reference_1_name}</p>
                        <p><span className="text-gray-500">Company:</span> {subbie.reference_1_company || 'N/A'}</p>
                        <p><span className="text-gray-500">Phone:</span> {subbie.reference_1_phone || 'N/A'}</p>
                        <p><span className="text-gray-500">Email:</span> {subbie.reference_1_email || 'N/A'}</p>
                      </div>
                    </div>
                  )}
                  {subbie.reference_2_name && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium text-navy mb-2">Reference 2</h3>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-500">Name:</span> {subbie.reference_2_name}</p>
                        <p><span className="text-gray-500">Company:</span> {subbie.reference_2_company || 'N/A'}</p>
                        <p><span className="text-gray-500">Phone:</span> {subbie.reference_2_phone || 'N/A'}</p>
                        <p><span className="text-gray-500">Email:</span> {subbie.reference_2_email || 'N/A'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* FLEET TAB */}
        {activeTab === 'fleet' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold" style={{ color: '#0A1929' }}>
                  <FaTruck className="inline mr-2" style={{ color: '#00A86B' }} />
                  Tractors ({trucks.length || 0})
                </h2>
              </div>
              {trucks && trucks.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {trucks.map((truck, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium text-navy">{truck.registration_number || 'N/A'}</span>
                        <span className="text-sm text-gray-500 ml-2">{truck.make || ''} {truck.model || ''}</span>
                      </div>
                      <span className="text-xs text-gray-400">{truck.year || ''}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">No tractors listed</p>
              )}
            </div>

            <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold" style={{ color: '#0A1929' }}>
                  <FaTrailer className="inline mr-2" style={{ color: '#00A86B' }} />
                  Trailers ({trailers.length || 0})
                </h2>
              </div>
              {trailers && trailers.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {trailers.map((trailer, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium text-navy">{trailer.registration_number || 'N/A'}</span>
                        <span className="text-sm text-gray-500 ml-2">{trailer.trailer_type || ''}</span>
                      </div>
                      <span className="text-xs text-gray-400">{trailer.capacity_mt ? `${trailer.capacity_mt} MT` : ''}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">No trailers listed</p>
              )}
            </div>

            <div className="bg-white rounded-xl border p-6 lg:col-span-2" style={{ borderColor: '#E8ECEF' }}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold" style={{ color: '#0A1929' }}>
                  <FaUserTie className="inline mr-2" style={{ color: '#00A86B' }} />
                  Drivers ({drivers.length || 0})
                </h2>
              </div>
              {drivers && drivers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {drivers.map((driver, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-medium text-navy">{driver.full_name || 'N/A'}</span>
                        <span className="text-sm text-gray-500 ml-2">ID: {driver.id_number || 'N/A'}</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        PrDP: {driver.prdp_number || 'N/A'}
                        {driver.prdp_expiry && ` | Expires: ${formatDate(driver.prdp_expiry)}`}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">No drivers listed</p>
              )}
            </div>

            <div className="bg-white rounded-xl border p-6 lg:col-span-2" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
                <FaShieldAlt className="inline mr-2" style={{ color: '#00A86B' }} />
                Fleet Capabilities
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Own Workshop:</span>
                  <span className={`text-sm font-medium ${subbie.has_own_workshop ? 'text-green-600' : 'text-gray-400'}`}>
                    {subbie.has_own_workshop ? '✅ Yes' : '❌ No'}
                  </span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">24hr Breakdown:</span>
                  <span className={`text-sm font-medium ${subbie.has_24hr_breakdown ? 'text-green-600' : 'text-gray-400'}`}>
                    {subbie.has_24hr_breakdown ? '✅ Yes' : '❌ No'}
                  </span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">GPS Tracking:</span>
                  <span className={`text-sm font-medium ${subbie.has_tracking_system ? 'text-green-600' : 'text-gray-400'}`}>
                    {subbie.has_tracking_system ? '✅ Yes' : '❌ No'}
                  </span>
                </div>
                {subbie.preferred_route && (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-500">Preferred Route:</span>
                    <span className="text-sm font-medium text-navy">{subbie.preferred_route}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DOCUMENTS TAB */}
        {activeTab === 'documents' && (
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold" style={{ color: '#0A1929' }}>
                <FaFileAlt className="inline mr-2" style={{ color: '#00A86B' }} />
                Uploaded Documents
              </h2>
              <div className="flex gap-2">
                <span className="text-sm text-gray-500">{documents.length} documents</span>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-3 py-1 bg-green text-white rounded-lg text-xs hover:bg-green transition-colors flex items-center gap-1"
                >
                  <FaUpload size={12} /> Upload
                </button>
              </div>
            </div>

            {documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc) => (
                  <div key={doc.key} className="border rounded-lg p-4 hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        {getDocumentIcon(doc.url)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-navy truncate">{doc.label}</p>
                        <span className="text-xs text-gray-400">{getDocumentLabel(doc.url)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewDocument(doc.url, doc.label)}
                        className="flex-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                      >
                        <FaEye size={12} /> View
                      </button>
                      <button
                        onClick={() => downloadDocument(doc.url, `${doc.label}.pdf`)}
                        className="flex-1 px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg text-xs hover:bg-gray-100 transition-colors flex items-center justify-center gap-1"
                      >
                        <FaDownload size={12} /> Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📁</div>
                <h3 className="text-lg font-semibold text-navy mb-2">No Documents Uploaded</h3>
                <p className="text-sm text-gray-500">This subcontractor hasn't uploaded any documents yet.</p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="mt-4 px-4 py-2 bg-green text-white rounded-lg text-sm hover:bg-green transition-colors flex items-center gap-2 mx-auto"
                >
                  <FaUpload size={14} /> Upload First Document
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============================================
          MODALS
      ============================================ */}
      
      {/* Edit Insurance Modal */}
      <EditInsuranceModal
        isOpen={showInsuranceModal}
        onClose={() => setShowInsuranceModal(false)}
        onSave={handleSaveInsurance}
        title="Insurance"
        type={insuranceType}
        data={insuranceData}
        subbieId={id}
        isSubbie={false}
      />

      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadDocument}
        subbieId={id}
        documentTypes={documentTypes}
      />
      {/* Delete Subcontractor Confirm */}
      <ConfirmDialog
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="Delete Subcontractor"
        message={`Are you sure you want to delete ${subbie?.company_name || 'this subcontractor'}? This action cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
      />
    </AdminLayout>
  );
}

export default SubcontractorDetailPage;